import { theme as defaultTheme } from '../themes/BGH/theme';
import { enhanceTheme } from '@bygghemmait/theme/server';
import { APP_INIT } from 'components/Pagelayout/actions';
import { applyStoreEvents } from 'components/Shared/communication';
import reducers, { initialState } from 'components/Shared/reducers';
import { linkInterceptor, reactInjector } from 'components/Shared/server';
import { BrowserHistory, createBrowserHistory } from 'history';
import { fromScript } from 'hypernova';
import type { ContextType } from 'hypernova/server';
import { HeaderState } from 'models/HeaderState';
import { PageState } from 'models/PageState';
import { DehydratedState } from '@tanstack/react-query';
import { applyMiddleware, compose, createStore, Middleware, Store } from 'redux';
import thunk from 'redux-thunk';
import appShell from 'store/appShell';
import { initBroadcast } from 'store/appShell/broadcast';
import { batchMiddleware } from 'store/batching';
import freeze from 'store/freeze';
import {
  googleTagManagerCheckoutMiddleware,
  googleTagManagerMiddleware
} from 'store/googleTagManager';
import { historySync, middleware as navigation } from 'store/history';
import { enhancer as responsiveEnhancer, mediaMiddleware } from 'store/responsive';
import { gecpMqHandler } from 'store/responsive/mediaQuery';
import { restoreArticleListMiddleware } from 'views/EditorialPage/middleware';
import { restoreProductListMiddleware } from 'views/ProductlistPage/middleware';
import type { ProviderProps } from '../Providers';
import { createQueryClient } from './createQueryClient';

export function createProviders(
  name: string,
  preloadedState: HeaderState = initialState,
  context: ContextType
): ProviderProps<PageState> {
  const client = createQueryClient(context);

  const theme = enhanceTheme(defaultTheme, gecpMqHandler(preloadedState));

  const currentState = reducers(initialState, { type: undefined });
  const composeEnhancers =
    process.env.TARGET === 'browser' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
      ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({})
      : compose;

  /**
   * Keep the googleTagManagerMiddleware last of the tagManager-middlewares.
   * This will ensure that the "PageLoad"-event is sent before other
   * APP_INIT-based events you might have.
   */
  const middleware: Middleware[] = [
    batchMiddleware,
    thunk,
    googleTagManagerCheckoutMiddleware,
    googleTagManagerMiddleware,
    restoreProductListMiddleware,
    restoreArticleListMiddleware
  ];

  if (process.env.TARGET === 'browser') {
    middleware.push(appShell());
  }
  let history: BrowserHistory | undefined;
  if (process.env.TARGET === 'browser') {
    history = createBrowserHistory();
    middleware.push(navigation(history));
    middleware.push(mediaMiddleware());
  }
  const enhancer = composeEnhancers(responsiveEnhancer(), applyMiddleware(...middleware, freeze()));

  const store: Store<PageState> = createStore(
    reducers,
    { ...currentState, ...preloadedState },
    enhancer
  );
  applyStoreEvents<PageState>(store);
  initBroadcast(store);

  if (process.env.TARGET === 'browser' && history) {
    historySync<HeaderState>(history, store, window);
  }

  if (process.env.TARGET === 'browser') {
    const whitelist = { ...store.getState().appShell.whitelist };
    window.onclick = linkInterceptor(reactInjector(whitelist, store));
    store.dispatch({ type: APP_INIT });
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-ignore
  if (window.Cypress) {
    // Enable Cypress to be able to read straight from Redux instead of trying to check the DOM

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    window.store = store;
  }

  const state = fromScript<DehydratedState>({ 'react-query-state-key': name.replace(/\W/g, '') });
  return { store, client, state, theme };
}
